import type { ReactNode } from 'react';
import type { FlagPauseNotice, FlagPlanState, FlagStage } from '@/state/flags/flag-status';
import type { FlagManager } from '@/state/flags/useFlag';
import type { Flag } from '@/state/flags/flag-types';
import { Fragment } from 'react';
import { PauseCircle, ShieldAlert } from 'lucide-react';
import { cn } from 'helpers/cn';
import { formatDate, formatDateTime } from 'helpers/formatters';
import { userFullName } from 'helpers/user-identity';
import { UserAvatar } from '@/components/UserAvatar';
import { percentOf } from '@/state/flags/flag-status';
import { INACTIVE_TONE, Line, Step, STEP_TONE } from '../../Experiments/Details/Timeline';
import { FLAG_COPY } from '../copy';

type FlagStatusCardProps = { manager: FlagManager; flag: Flag };

const COPY = FLAG_COPY.status;

/** The flag's stages on the experiment Timeline's tones: draft, live as running, archived as ended. */
const STAGE_TONE: Record<FlagStage, string> = { draft: 'draft', live: 'running', archived: 'ended' };
const STAGES: readonly FlagStage[] = ['draft', 'live', 'archived'];

const planText = (plan: FlagPlanState) => {
  if (plan.kind === 'none') return COPY.noPlan;
  if (plan.kind === 'not-started') return COPY.planStates.none;
  if (plan.kind === 'done') return COPY.planStates.done;
  return COPY.planStates[plan.kind](plan.step, plan.total);
};

/**
 * The flag's counterpart of the experiment Timeline, in its place under the name: the stage as
 * a track (draft, live, archived), the share that gets On, where the plan is and what happens
 * next. A paused plan is explained here, once: why it stopped and what to do. The cards below
 * carry a status chip at most.
 */
export const FlagStatusCard = ({ manager, flag }: FlagStatusCardProps) => {
  const summary = manager.summary;
  if (!summary) return null;
  const { stage, plan, pause } = summary;
  const tone = STEP_TONE[STAGE_TONE[stage]] ?? INACTIVE_TONE;
  const creator = flag.createdByUser;
  const stageIndex = STAGES.indexOf(stage);
  const dates: Record<FlagStage, number | null | undefined> = {
    draft: flag.createdAt,
    live: flag.startsAt,
    archived: flag.endsAt,
  };
  const share = stage === 'draft' ? COPY.notLive : percentOf(summary.onBp);
  const next = nextText({ manager, stage, pause });

  return (
    <section className="bg-panel border-line rounded-panel border" data-tour="flag-status">
      <div className="border-line bg-panel2 rounded-t-panel flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 border-b px-4 py-2.5">
        <span className="flex min-w-0 items-center gap-2">
          <span className={cn('size-[5px] shrink-0 rounded-pill', tone.dot, stage === 'live' && 'animate-pulse')} />
          <span className={cn('font-mono text-nano tracking-[0.1em] lowercase', tone.label)}>{COPY.stages[stage]}</span>
          {stage === 'live' && (
            <>
              <span className="text-dim font-mono text-nano">·</span>
              <span className={cn('truncate font-mono text-micro', tone.label)}>
                {COPY.getOn(percentOf(summary.onBp))}
              </span>
            </>
          )}
        </span>
        {!!creator && (
          <span className="micro-label flex min-w-0 items-center gap-1.5" title={userFullName(creator)}>
            <span className="shrink-0">{COPY.createdBy}</span>
            <UserAvatar user={creator} size="xs" />
            <span className="truncate">{creator.firstName || userFullName(creator)}</span>
          </span>
        )}
      </div>

      <div className="flex flex-col gap-4 p-4">
        {/* Stacked on a phone, a track from md: three stages and two connectors fit a row there. */}
        <div className="flex flex-col gap-2 md:hidden">
          {STAGES.map((key, index) => (
            <Step
              key={key}
              stepKey={STAGE_TONE[key]}
              label={COPY.stages[key]}
              date={index <= stageIndex && dates[key] ? formatDate(dates[key]) : undefined}
              active={index <= stageIndex}
            />
          ))}
        </div>
        <div className="hidden min-h-[48px] items-center gap-3 md:flex">
          {STAGES.map((key, index) => {
            const nextKey = STAGES[index + 1];
            return (
              <Fragment key={key}>
                <Step
                  stepKey={STAGE_TONE[key]}
                  label={COPY.stages[key]}
                  date={index <= stageIndex && dates[key] ? formatDate(dates[key]) : undefined}
                  active={index <= stageIndex}
                />
                {nextKey && <Line stepKey={STAGE_TONE[nextKey]} active={index + 1 <= stageIndex} />}
              </Fragment>
            );
          })}
        </div>

        <dl className="border-line m-0 grid gap-3 border-t pt-3 sm:grid-cols-3">
          <Fact label={COPY.rollout} value={share} />
          <Fact label={COPY.plan} value={planText(plan)} />
          <Fact label={COPY.next} value={next} />
        </dl>

        {pause && <PauseNotice pause={pause} />}
      </div>
    </section>
  );
};

type NextTextParams = { manager: FlagManager; stage: FlagStage; pause: FlagPauseNotice };

/** The card's Next: a scheduled publish or turn off, the plan's next step, or what waits on a person. */
function nextText({ manager, stage, pause }: NextTextParams): string {
  const { schedule } = manager;
  if (stage === 'archived') return COPY.archivedNext;
  if (stage === 'draft' && schedule?.publishAt) return COPY.publishesAt(formatDateTime(schedule.publishAt));
  if (pause) return COPY.waiting;
  if (manager.next) return manager.next;
  if (stage === 'live' && schedule?.offAt) return COPY.turnsOffAt(formatDateTime(schedule.offAt));
  return COPY.nothingNext;
}

type FactProps = { label: string; value: ReactNode };

const Fact = ({ label, value }: FactProps) => (
  <div className="flex min-w-0 flex-col gap-1">
    <dt className="micro-label">{label}</dt>
    <dd className="text-foreground m-0 truncate text-body">{value}</dd>
  </div>
);

type PauseNoticeProps = { pause: NonNullable<FlagPauseNotice> };

/**
 * Why the plan is paused, and what to do: a breach in the danger tones (full hairline, wash and
 * icon, never a coloured edge), a pause by hand or by Turn off in the warn ones. The guardrail's
 * own words come from run-flag-guardrails.ts.
 */
const PauseNotice = ({ pause }: PauseNoticeProps) => {
  const isGuardrail = pause.kind === 'guardrail';
  const Icon = isGuardrail ? ShieldAlert : PauseCircle;
  return (
    <div
      role="status"
      className={cn(
        'rounded-card flex items-start gap-2 border p-3',
        isGuardrail ? 'bg-danger-bg border-danger-line text-danger' : 'bg-warn-bg border-warn-line text-warn',
      )}
    >
      <Icon className="mt-0.5 size-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
      <div className="flex min-w-0 flex-col gap-1">
        <p className="m-0 text-body font-medium">{isGuardrail ? COPY.guardrailPaused : COPY.planPaused}</p>
        {(pause.reason || isGuardrail) && <p className="m-0 text-meta">{pause.reason ?? COPY.guardrailPausedReason}</p>}
        <p className="text-dim m-0 text-meta">{isGuardrail ? COPY.guardrailPausedHelp : COPY.planPausedHelp}</p>
      </div>
    </div>
  );
};
